/**
 * The browser's view of the BFF. Same-origin fetch, cookie attached automatically,
 * no token handling anywhere — the whole point of the BFF design.
 *
 * Every non-2xx becomes an ApiError carrying the server's short error code, so the
 * UI can react to specific cases (SCHEMA_STALE, validation_failed, unauthenticated)
 * without parsing messages.
 */

/**
 * @typedef {object} Me
 * @property {string} tid
 * @property {string} oid
 * @property {string | null} name
 * @property {string | null} email
 * @property {string[]} roles
 * @property {string[]} formIds
 * @property {string} sessionExpiresAt
 * @property {{ directorySearch: boolean }} [features] What the UI may attempt.
 */

/**
 * One row of a directory search.
 *
 * @typedef {object} DirectoryUser
 * @property {string} userPrincipalName
 * @property {string} displayName
 * @property {string} [mail]
 * @property {string} [jobTitle]
 */

/**
 * The case the gateway created, passed through by the BFF exactly as Dynamics reports it.
 *
 * The same shape for every form: one routed to the automation service still files a
 * case, and that case is all the browser hears about. Where else the submission went is
 * the server's and the technician's business, not the receipt's.
 *
 * @typedef {object} CaseCreated
 * @property {string} caseId Dynamics case GUID.
 * @property {string} ticketNumber The human reference, e.g. `CAS-01234-X1Y2Z3`.
 * @property {string} title
 * @property {string} createdOn ISO 8601.
 */

/**
 * What comes back while the server runs in dry-run mode: the gateway validated the
 * case and resolved the account, then wrote nothing. No ticket number exists.
 *
 * @typedef {object} CaseDryRun
 * @property {true} dryRun
 * @property {string} title
 * @property {string} customerNumber
 * @property {string} accountName The Dynamics account the case would have been filed under.
 */

/** @typedef {CaseCreated | CaseDryRun} SubmissionResult */

/**
 * Shape of z.treeifyError for an object schema: per-field message lists.
 *
 * @typedef {object} IssueTree
 * @property {string[]} errors
 * @property {Record<string, { errors: string[] }>} [properties]
 */

export class ApiError extends Error {
  constructor(status, code, body) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }

  /** Field → first message, for 400 validation_failed responses. */
  fieldErrors() {
    const tree = this.body?.issues;
    const out = {};
    for (const [field, node] of Object.entries(tree?.properties ?? {})) {
      const first = node.errors[0];
      if (first) out[field] = first;
    }
    return out;
  }
}

async function request(url, init = {}) {
  const response = await fetch(url, {
    ...init,
    headers: { accept: 'application/json', ...init.headers },
    credentials: 'same-origin',
  });
  const body = response.status === 204 ? undefined : await response.json().catch(() => undefined);
  if (!response.ok) {
    const code = body?.error ?? `http_${response.status}`;
    throw new ApiError(response.status, code, body);
  }
  return body;
}

/**
 * Who is signed in, or null if nobody.
 *
 * @returns {Promise<Me | null>}
 */
export async function getMe() {
  try {
    return await request('/api/me');
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export async function logout() {
  await request('/auth/logout', { method: 'POST' });
}

/**
 * The signed-in user's tenant, for the picker to hold and match locally. `truncated`
 * means the tenant was too large to list in full, and the picker should also search
 * live for what is typed.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ users: DirectoryUser[], truncated: boolean }>}
 */
export async function listDirectoryUsers(options = {}) {
  return request('/api/directory/users', { signal: options.signal });
}

/**
 * Users in the signed-in user's tenant matching a few typed characters. The BFF does
 * the searching with the user's own directory permission; no token is here.
 *
 * @param {string} q At least two characters.
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<DirectoryUser[]>}
 */
export async function searchDirectoryUsers(q, options = {}) {
  const body = await request(`/api/directory/users?${new URLSearchParams({ q })}`, {
    signal: options.signal,
  });
  return body.users;
}

/**
 * One user's current attributes, keyed by the catalogue's names. Absent keys are
 * unset on the user.
 *
 * @param {string} userPrincipalName
 * @returns {Promise<Record<string, string | string[]>>}
 */
export async function getDirectoryUser(userPrincipalName) {
  const body = await request(`/api/directory/users/${encodeURIComponent(userPrincipalName)}`);
  return body.user;
}

/**
 * @typedef {object} SubmitInput
 * @property {string} formId
 * @property {string} formVersion
 * @property {Record<string, unknown>} payload
 * @property {string} idempotencyKey Reused across retries of the same submission so
 *   the gateway never stores it twice.
 */

/**
 * @param {SubmitInput} input
 * @returns {Promise<SubmissionResult>}
 */
export async function submitForm(input) {
  return request('/api/submissions', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'idempotency-key': input.idempotencyKey },
    body: JSON.stringify({
      formId: input.formId,
      formVersion: input.formVersion,
      payload: input.payload,
    }),
  });
}
